import { useCallback, useMemo } from 'react'
import { ColumnOption } from './types'

interface UseDropdownOptionsProps {
  options: ColumnOption[]
  onChange: (options: ColumnOption[]) => void
  generateOptionId: () => string
}

export default function useDropdownOptions({
  options,
  onChange,
  generateOptionId,
}: UseDropdownOptionsProps) {
  const visibleOptions = useMemo(
    () => options.filter((opt) => !opt._destroy),
    [options]
  )

  const moveOption = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const visible = options.filter((opt) => !opt._destroy)
      const draggedOption = visible[dragIndex]

      const newVisible = [...visible]
      newVisible.splice(dragIndex, 1)
      newVisible.splice(hoverIndex, 0, draggedOption)

      const reordered = newVisible.map((opt, idx) => ({
        ...opt,
        position: idx + 1,
      }))

      const destroyed = options.filter((opt) => opt._destroy)
      onChange([...reordered, ...destroyed])
    },
    [options, onChange]
  )

  const updateOption = useCallback(
    (id: number | string, updates: Partial<ColumnOption>) => {
      onChange(
        options.map((opt) => (opt.id === id ? { ...opt, ...updates } : opt))
      )
    },
    [options, onChange]
  )

  const removeOption = useCallback(
    (id: number | string) => {
      const option = options.find((opt) => opt.id === id)
      if (!option) return

      if (typeof option.id === 'number') {
        onChange(options.map((opt) => (opt.id === id ? { ...opt, _destroy: true } : opt)))
      } else {
        onChange(options.filter((opt) => opt.id !== id))
      }
    },
    [options, onChange]
  )

  const addOption = useCallback(() => {
    const maxPosition = Math.max(0, ...visibleOptions.map((opt) => opt.position))
    const newOption: ColumnOption = {
      id: generateOptionId(),
      label: '',
      value: '',
      color: '#ffffff',
      position: maxPosition + 1,
    }
    onChange([...options, newOption])
  }, [options, visibleOptions, onChange, generateOptionId])

  return {
    visibleOptions,
    moveOption,
    updateOption,
    removeOption,
    addOption,
  }
}
